import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Scale, Volume2, FileText, Mic, MicOff, Sparkles, Download, Trash2,
  ShieldAlert, CheckCircle2, Clock, AlertTriangle, Camera, Image as ImageIcon, X,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  open: { icon: Clock, label: 'Open', cls: 'bg-amber-950 border-amber-600 text-amber-300' },
  in_review: { icon: ShieldAlert, label: 'In review', cls: 'bg-cyan-950 border-cyan-600 text-cyan-300' },
  resolved: { icon: CheckCircle2, label: 'Resolved', cls: 'bg-emerald-950 border-emerald-600 text-emerald-300' },
};
const SEVERITY_STYLE = {
  low: 'bg-slate-800 border-slate-600 text-slate-300',
  medium: 'bg-amber-950 border-amber-600 text-amber-300',
  high: 'bg-rose-950 border-rose-600 text-rose-300',
};

function AuthImage({ src, headers, alt, className, onError }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    fetch(src, { headers })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(b => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(b);
        setUrl(objUrl);
      })
      .catch(() => onError && onError());
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [src]);
  if (!url) return <div className={`${className} bg-slate-900 animate-pulse`} />;
  return <img src={url} alt={alt} className={className} />;
}

export default function HrDetail() {
  const { id } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null); // { fileId, filename }
  const [photoBlobUrl, setPhotoBlobUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCase = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/cases/${id}`, { headers });
      setCaseData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchCase(); }, [id]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await uploadVoice(blob, 'recording.webm');
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const uploadVoice = async (fileOrBlob, name) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', fileOrBlob, name);
    try {
      await axios.post(`${API_URL}/api/hr/cases/${id}/voice`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Voice uploaded and transcribed');
      fetchCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Voice upload failed');
    } finally {
      setUploading(false);
    }
  };

  const uploadDocument = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/hr/cases/${id}/document`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      fetchCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Document upload failed');
    } finally {
      setUploading(false);
    }
  };

  const uploadPhotos = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(fileList).forEach(f => fd.append('files', f));
    try {
      const res = await axios.post(`${API_URL}/api/hr/cases/${id}/photos`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${res.data.uploaded} photo${res.data.uploaded === 1 ? '' : 's'} uploaded`);
      fetchCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openPhoto = async (file) => {
    try {
      const resp = await fetch(`${API_URL}/api/hr/cases/${id}/file/${file.id}`, { headers });
      const blob = await resp.blob();
      setPhotoBlobUrl(URL.createObjectURL(blob));
      setPhotoPreview({ fileId: file.id, filename: file.filename });
    } catch {
      toast.error('Failed to load photo');
    }
  };
  const closePhoto = () => {
    if (photoBlobUrl) URL.revokeObjectURL(photoBlobUrl);
    setPhotoBlobUrl(null);
    setPhotoPreview(null);
  };

  const analyzeCase = async () => {
    setAnalyzing(true);
    try {
      await axios.post(`${API_URL}/api/hr/cases/${id}/analyze`, {}, { headers });
      toast.success('AI mediation outcome generated');
      fetchCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API_URL}/api/hr/cases/${id}/status`, { status }, { headers });
      toast.success(`Status: ${status}`);
      fetchCase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const downloadFile = (fileId, filename) => {
    // Use fetch with token then create blob URL to preserve auth
    fetch(`${API_URL}/api/hr/cases/${id}/file/${fileId}`, { headers })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Download failed'));
  };

  if (loading) return <DashboardLayout><div className="text-slate-500 py-8 text-center">Loading…</div></DashboardLayout>;
  if (!caseData) return <DashboardLayout><div className="text-slate-500 py-8 text-center">Case not found</div></DashboardLayout>;

  const st = STATUS_STYLE[caseData.status] || STATUS_STYLE.open;
  const StatusIcon = st.icon;
  const voiceFiles = (caseData.files || []).filter(f => f.kind === 'voice');
  const docFiles = (caseData.files || []).filter(f => f.kind === 'document');
  const photoFiles = (caseData.files || []).filter(f => f.kind === 'photo');
  const outcome = caseData.outcome;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-4" data-testid="hr-detail-page">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/hr')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to cases
          </Button>
          <Select value={caseData.status} onValueChange={updateStatus}>
            <SelectTrigger className="bg-slate-900 border-slate-700 text-white w-40" data-testid="hr-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-white">
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_review">In review</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-white flex items-center gap-2">
                  <Scale className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                  <span className="break-words">{caseData.title}</span>
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <Badge className={`${st.cls}`}>
                    <StatusIcon className="h-3 w-3 mr-1" /> {st.label}
                  </Badge>
                  <Badge className="bg-slate-900 border-slate-600 text-slate-400">{caseData.category}</Badge>
                  {outcome?.severity && (
                    <Badge className={`${SEVERITY_STYLE[outcome.severity]} uppercase`}>
                      severity: {outcome.severity}
                    </Badge>
                  )}
                </div>
                <div className="text-slate-400 text-xs mt-2">
                  Parties: <span className="text-slate-300">{caseData.involved_parties}</span>
                </div>
                <div className="text-slate-500 text-[11px] mt-1">
                  Created {new Date(caseData.created_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
                  {caseData.created_by_name && <> by {caseData.created_by_name}</>}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-slate-300 text-sm whitespace-pre-wrap" data-testid="hr-description">
              {caseData.description}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Voice evidence */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Volume2 className="h-4 w-4 text-cyan-400" /> Voice ({voiceFiles.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {!recording ? (
                  <Button onClick={startRecording} disabled={uploading}
                          className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="hr-start-record-btn">
                    <Mic className="h-4 w-4 mr-1" /> Record
                  </Button>
                ) : (
                  <Button onClick={stopRecording}
                          className="bg-rose-700 hover:bg-rose-800 text-white animate-pulse" data-testid="hr-stop-record-btn">
                    <MicOff className="h-4 w-4 mr-1" /> Stop
                  </Button>
                )}
                <label className="inline-flex">
                  <input type="file" accept="audio/*" className="hidden"
                         onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVoice(f, f.name); e.target.value = ''; }}
                         data-testid="hr-voice-file-input" />
                  <Button asChild variant="outline" className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-700" disabled={uploading}>
                    <span><Volume2 className="h-4 w-4 mr-1" /> Upload audio</span>
                  </Button>
                </label>
              </div>
              {voiceFiles.length === 0 ? (
                <div className="text-slate-500 text-xs">No voice recordings yet.</div>
              ) : (
                <div className="space-y-2">
                  {voiceFiles.map(f => (
                    <div key={f.id} className="border border-slate-700 rounded p-2 space-y-1" data-testid={`hr-voice-${f.id}`}>
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-200 truncate">{f.filename}</span>
                        <Button size="sm" variant="ghost" onClick={() => downloadFile(f.id, f.filename)} className="h-6 w-6 p-0 text-slate-400 hover:text-white">
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="text-[10px] text-slate-500">{new Date(f.uploaded_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })} · {(f.size / 1024).toFixed(0)} KB</div>
                      {f.transcript && (
                        <div className="bg-slate-900/60 border border-slate-700 rounded p-2 text-[11px] text-slate-300 leading-relaxed">
                          <span className="text-slate-500 mr-1">Transcript:</span>{f.transcript}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-cyan-400" /> Documents ({docFiles.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="inline-flex">
                <input type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp" className="hidden"
                       onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDocument(f); e.target.value = ''; }}
                       data-testid="hr-doc-file-input" />
                <Button asChild variant="outline" className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-700" disabled={uploading}>
                  <span><FileText className="h-4 w-4 mr-1" /> Upload document</span>
                </Button>
              </label>
              {docFiles.length === 0 ? (
                <div className="text-slate-500 text-xs">No documents yet.</div>
              ) : (
                <div className="space-y-2">
                  {docFiles.map(f => (
                    <div key={f.id} className="flex items-center justify-between border border-slate-700 rounded p-2" data-testid={`hr-doc-${f.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-slate-200 text-xs truncate">{f.filename}</div>
                        <div className="text-[10px] text-slate-500">{new Date(f.uploaded_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })} · {(f.size / 1024).toFixed(0)} KB</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => downloadFile(f.id, f.filename)} className="h-7 w-7 p-0 text-slate-400 hover:text-white">
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Photo evidence */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Camera className="h-4 w-4 text-cyan-400" /> Photo evidence ({photoFiles.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex">
                <input type="file" accept="image/*" capture="environment" className="hidden"
                       onChange={(e) => { if (e.target.files?.length) uploadPhotos(e.target.files); e.target.value = ''; }}
                       data-testid="hr-photo-capture-input" />
                <Button asChild className="bg-cyan-600 hover:bg-cyan-700 text-white" disabled={uploading}>
                  <span><Camera className="h-4 w-4 mr-1" /> Take photo</span>
                </Button>
              </label>
              <label className="inline-flex">
                <input type="file" accept="image/*" multiple className="hidden"
                       onChange={(e) => { if (e.target.files?.length) uploadPhotos(e.target.files); e.target.value = ''; }}
                       data-testid="hr-photo-multi-input" />
                <Button asChild variant="outline" className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-700" disabled={uploading}>
                  <span><ImageIcon className="h-4 w-4 mr-1" /> Upload photos</span>
                </Button>
              </label>
              <span className="text-[10px] text-slate-500 self-center">jpg, png, webp, heic, gif · up to 15 MB each</span>
            </div>
            {photoFiles.length === 0 ? (
              <div className="text-slate-500 text-xs">No photos yet.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {photoFiles.map(f => (
                  <button key={f.id} type="button" onClick={() => openPhoto(f)}
                          className="group relative aspect-square border border-slate-700 rounded overflow-hidden hover:border-cyan-500 transition-colors"
                          data-testid={`hr-photo-${f.id}`}
                          title={f.filename}>
                    <AuthImage src={`${API_URL}/api/hr/cases/${id}/file/${f.id}`}
                               headers={headers}
                               alt={f.filename}
                               className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1">
                      <span className="text-[9px] text-white truncate w-full">{f.filename}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Analysis */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-cyan-800/50">
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-cyan-400" /> AI Mediation Outcome
              </CardTitle>
              <Button onClick={analyzeCase} disabled={analyzing}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-50" data-testid="hr-analyze-btn">
                <Sparkles className="h-4 w-4 mr-1" />
                {analyzing ? 'Analyzing…' : outcome ? 'Re-analyze' : 'Analyze with GPT-5.2'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!outcome ? (
              <div className="text-slate-500 text-sm text-center py-6" data-testid="hr-no-outcome">
                Click <span className="text-cyan-400 font-semibold">Analyze</span> once you&apos;ve added enough evidence. The AI will produce a neutral summary, severity, mediation suggestions and next steps.
              </div>
            ) : (
              <div className="space-y-4" data-testid="hr-outcome">
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Summary</div>
                  <div className="text-slate-200 text-sm leading-relaxed">{outcome.summary}</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Severity</div>
                    <Badge className={`${SEVERITY_STYLE[outcome.severity]} text-base uppercase`}>{outcome.severity}</Badge>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">AI Category</div>
                    <div className="text-slate-200">{outcome.category_ai}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Generated</div>
                    <div className="text-slate-400 text-xs">{new Date(outcome.generated_at).toLocaleString('en-ZA')}</div>
                  </div>
                </div>
                <OutcomeList title="Key points" icon={AlertTriangle} items={outcome.key_points} colour="text-amber-300" />
                <OutcomeList title="Mediation suggestions" icon={Scale} items={outcome.mediation_suggestions} colour="text-cyan-300" />
                <OutcomeList title="Next steps" icon={CheckCircle2} items={outcome.next_steps} colour="text-emerald-300" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {photoPreview && photoBlobUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
             onClick={closePhoto}
             data-testid="hr-photo-lightbox">
          <button type="button" onClick={closePhoto}
                  className="absolute top-4 right-4 text-white bg-slate-800 hover:bg-slate-700 rounded-full p-2"
                  aria-label="Close">
            <X className="h-5 w-5" />
          </button>
          <div className="max-w-full max-h-full flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <img src={photoBlobUrl} alt={photoPreview.filename}
                 className="max-w-[90vw] max-h-[80vh] object-contain rounded shadow-2xl" />
            <div className="text-slate-200 text-sm flex items-center gap-3">
              <span>{photoPreview.filename}</span>
              <a href={photoBlobUrl} download={photoPreview.filename}
                 className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200">
                <Download className="h-4 w-4" /> Download
              </a>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function OutcomeList({ title, icon: Icon, items, colour }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1 ${colour}`}>
        <Icon className="h-3 w-3" /> {title}
      </div>
      <ul className="list-disc pl-5 space-y-1 text-sm text-slate-200">
        {items.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </div>
  );
}
